import React from 'react';
import './chat.css';
import { isMobileViewport } from '../../../utils/responsive/viewport';

import ChatWindow from '../p2p_chat_window';

import { createState } from 'state-pool';
import SecurityCheckPopup from './security_check_popup';
import Channel from '../../../myt_chat/chat/models/Channel';
import ChannelType from '../../../myt_chat/chat/enums/ChannelType';
import { ChannelDBOps } from '../../../myt_chat/worker/chatProxies';
import ChannelUtils from '../../../myt_chat/chat/utils/ChannelUtils';
import UserRole from '../../../myt_chat/chat/enums/UserRole';
import { AccountHandler } from '../../../myt_chat/worker/chatProxies';
import { UserDBOps } from '../../../myt_chat/worker/chatProxies';
import { MessageHandler } from '../../../myt_chat/worker/chatProxies';
import Packet from '../../../myt_chat/chat/enums/Packet';
import { db } from '../../../myt_chat/chat/database/MytChatDB';

type ChatWindowItem = {
  channel: Channel;
  openState: ChatOpenState;
};

export type ChatWindowManagerGlobalState = {
  windows: Array<ChatWindowItem>;
};

type ChatState = {
  chat_count: number;
  windows: Array<ChatWindowItem>;
  contentViolationPopup: boolean;
};

type ChatProps = {};

export enum ChatOpenState {
  CLOSED = 0,
  MINIMIZED = 1,
  OPEN = 2
}

// Hard cap on concurrently mounted chat popups. Each popup carries 4 Dexie
// liveQuery subscriptions + a Virtuoso instance, so cost is structural even
// when individual popups are idle. Width-based slicing still applies on top.
export const MAX_MOUNTED_POPUPS = 3;

function createDefaultState(): ChatWindowManagerGlobalState {
  return {
    windows: new Array<ChatWindowItem>()
  };
}

export const chatWindowManagerState = createState(createDefaultState()); // Create store for storing our global state

/**
 * Optimized function to open chat with a user UUID.
 * Uses cached data to open chat immediately, then fetches fresh profile data in background.
 *
 * @param userUUID - The UUID of the user to chat with
 * @param userRole - The role of the user
 * @param ticketData - Optional CareTicket data to use for immediate channel creation
 */
export const openChatWithUserUUID = async (
  userUUID: string,
  userRole: UserRole,
  ticketData?: { name: string; avatar: string; role: UserRole }
) => {
  console.log(`openChatWithChannelUUID: ${userUUID}, opening with cache...`);

  const channelId = ChannelUtils.makeChannel(userUUID, userRole);

  let channel = await ChannelDBOps.shared.getChannel(channelId);
  // console.log(`Channel lookup for ${userUUID} returned:`, channel);

  if (channel === undefined) {
    const cachedUser = await UserDBOps.shared.getUser(userUUID);

    if (cachedUser) {
      channel = new Channel();
      channel.uuid = channelId;
      channel.type = ChannelType.P2P;
      channel.otherUserId = userUUID;
      channel.name = cachedUser.name || '';
      channel.avatar = cachedUser.avatar || '';
      channel.isInviteActionPending = Packet.Values.FALSE;
      await db.channel.put(channel);
    } else if (ticketData) {
      channel = new Channel();
      channel.uuid = channelId;
      channel.type = ChannelType.P2P;
      channel.otherUserId = userUUID;
      channel.name = ticketData.name || '';
      channel.avatar = ticketData.avatar || '';
      channel.isInviteActionPending = Packet.Values.FALSE;
      await db.channel.put(channel);
    }
  } else {
    if (ticketData && (!channel.name || !channel.avatar)) {
      const needsUpdate =
        (!channel.name && ticketData.name) || (!channel.avatar && ticketData.avatar);

      if (needsUpdate) {
        const updates: Partial<Channel> = {};
        if (!channel.name && ticketData.name) {
          updates.name = ticketData.name;
        }
        if (!channel.avatar && ticketData.avatar) {
          updates.avatar = ticketData.avatar;
        }
        await db.channel.update(channelId, updates);
        channel = await ChannelDBOps.shared.getChannel(channelId);
      }
    }
  }

  if (channel) {
    openChatWindow(channel);

    AccountHandler.shared.fetchUserProfile(userUUID).catch((error) => {
      console.warn(`Failed to fetch fresh profile for ${userUUID}:`, error);
    });
  } else {
    console.log(`No cached data found for ${userUUID}, fetching profile...`);
    try {
      await AccountHandler.shared.fetchUserProfile(userUUID);
      channel = await ChannelDBOps.shared.getChannel(channelId);
      if (channel) {
        openChatWindow(channel);
      } else {
        console.log(`openChatWithChannelUUID: ${userUUID} failed, channel not found after fetch`);
      }
    } catch (error) {
      console.error(`Failed to open chat for ${userUUID}:`, error);
    }
  }
};

/**
 * Sends a text message to a user (by UUID + role) without opening a chat popup.
 * Resolves the P2P channel from cache (creating a minimal one if needed), then
 * hands the message to the chat worker. Returns false for empty text / no
 * resolvable channel, true once the send is dispatched.
 */
export const sendMessageToUserUUID = async (
  userUUID: string,
  userRole: UserRole,
  text: string
): Promise<boolean> => {
  const trimmed = (text || '').trim();
  if (!userUUID || trimmed.length === 0) {
    return false;
  }

  const channelId = ChannelUtils.makeChannel(userUUID, userRole);
  let channel = await ChannelDBOps.shared.getChannel(channelId);

  if (channel === undefined) {
    channel = new Channel();
    channel.uuid = channelId;
    channel.type = ChannelType.P2P;
    channel.otherUserId = userUUID;
    channel.isInviteActionPending = Packet.Values.FALSE;
    const cachedUser = await UserDBOps.shared.getUser(userUUID);
    if (cachedUser) {
      channel.name = cachedUser.name || '';
      channel.avatar = cachedUser.avatar || '';
    }
    await db.channel.put(channel);
  }

  if (!channel) {
    return false;
  }

  await MessageHandler.shared.sendMessage(channel, trimmed);
  return true;
};

const openChatWindow = (channelItem: Channel) => {
  chatWindowManagerState.updateValue(
    (oldState: ChatWindowManagerGlobalState): ChatWindowManagerGlobalState => {
      let windowItemIndex = oldState.windows.findIndex(
        (item) => item.channel.uuid === channelItem.uuid
      );

      if (windowItemIndex === -1) {
        const newItem = {
          channel: channelItem,
          openState: ChatOpenState.OPEN
        };
        oldState.windows.unshift(newItem);
      } else {
        const item = oldState.windows[windowItemIndex];
        item.openState = ChatOpenState.OPEN;
        item.channel = channelItem;

        oldState.windows.splice(windowItemIndex, 1);
        oldState.windows.unshift(item);
      }

      // Drop the oldest popups beyond the cap. Without this, extra clicks queue
      // invisibly behind the visible set and cause flicker when one closes.
      while (oldState.windows.length > MAX_MOUNTED_POPUPS) {
        oldState.windows.pop();
      }

      return oldState;
    }
  );
};

export class ChatWindowManager extends React.Component<ChatProps, ChatState> {
  constructor(props: ChatProps) {
    super(props);
    this.state = {
      // Mobile shows exactly one full-screen window; desktop fits as many 400px
      // windows (340px window + 50px margin + 10px buffer) as the width allows
      // (min 1 — Math.floor could otherwise be 0 on a ~375px screen and render
      // nothing).
      chat_count: isMobileViewport() ? 1 : Math.max(1, Math.floor(window.innerWidth / 400)),
      windows: [],
      contentViolationPopup: false
    };
  }

  subscription: undefined | (() => void);

  updateChatCount = () => {
    const next = isMobileViewport() ? 1 : Math.max(1, Math.floor(window.innerWidth / 400));
    if (next !== this.state.chat_count) {
      this.setState({ chat_count: next });
    }
  };

  componentDidMount(): void {
    this.subscription = chatWindowManagerState.subscribe(
      (newValue: ChatWindowManagerGlobalState) => {
        this.setState({
          windows: newValue.windows
        });
      }
    );
    window.addEventListener('resize', this.updateChatCount);
    window.addEventListener('orientationchange', this.updateChatCount);
  }

  renderContentViolation = () => {
    this.setState({ contentViolationPopup: true });
  };

  closeContentViolation = () => {
    this.setState({ contentViolationPopup: false });
  };

  componentWillUnmount() {
    this.subscription?.();
    window.removeEventListener('resize', this.updateChatCount);
    window.removeEventListener('orientationchange', this.updateChatCount);
  }

  render_chat_windows = () => {
    const cap = Math.min(MAX_MOUNTED_POPUPS, this.state.chat_count);

    // openChatWindow unshifts new items at index 0, so the front of the array
    // is the newest. Render front-to-cap, not tail.
    const slice = this.state.windows.slice(0, cap);
    return slice.map((item) => {
      if (item.channel.type === ChannelType.P2P) {
        return <ChatWindow channel={item.channel} key={item.channel.uuid} />;
      }

      return null;
    });
  };

  render() {
    return (
      <>
        <div className="ch-cws">{this.render_chat_windows()}</div>
        {this.state.contentViolationPopup ? (
          <SecurityCheckPopup close={this.closeContentViolation} />
        ) : null}
      </>
    );
  }
}
